import Immutable from 'seamless-immutable';
import _ from 'lodash';

import RefData from 'ref-data';
import { embedDestinationsModes } from '../../common/enums';
import { assetRefService } from '../../common/Services';

/**
 * DisplayUnit
 * @typedef {Object} DisplayUnit
 * @property {string} assetRef - assetRef for the display unit icon
 * @property {RefData} channelTypeRef - the channel type ref
 * @property {string} dimensions - returns the outward facing dimensions or "dynamic" if isResponsive is true
 * @property {string} embedType - the type of embed this display unit is
 * @property {number} height - the display unit's supported height
 * @property {string} icon - the display unit icon
 * @property {number} id - the display unit id
 * @property {boolean} isAdUnit - is the display unit an ad type unit
 * @property {boolean} isResponsive - is the display unit responsive
 * @property {boolean} isStoryUnit - is the display unit a story type unit
 * @property {boolean} isWebUnit - is the display unit a web embed type
 * @property {string} name - the display unit name
 * @property {string} summary - the display unit summary
 * @property {RefData} typeRef - the display unit type ref
 * @property {number} width - the display unit's supported width
 * @property {string} ratio - the display unit's aspect ratio (used for Responsive layout)
 * @property {string} xDimension - the x-axis dimension, abstracts away needing to check whether it isResponsive to determine whether to read from the ratio or width
 * @property {string} yDimension - the y-axis dimension, abstracts away needing to check whether it isResponsive to determine whether to read from the ratio or height
 * @property {string} byline - byline text for a display unit option
 * @property {string} validFor - defines which kind of experience should display this display unit. Possible values: format.all, format.micro, format.macro
 */

/**
 * Create a DisplayUnitModel from provided data
 */
function create(data = {}) {
  const result = _.clone(data);
  result.isResponsive = data.growable;
  delete result.growable;
  result.typeRef = RefData.Model.hydrate(data.typeRef);
  result.channelTypeRef = RefData.Model.hydrate(data.format);
  result.status = RefData.Model.hydrate(data.status);
  delete result.format;

  return Immutable(result, { prototype: model });
}

/**
 * Transforms a backend displayUnit object into a frontend DisplayUnitModel
 * @param {Object} displayUnit - backend formatted displayUnit
 * @returns {DisplayUnit}
 */
function deserialize(displayUnit) {
  const result = _.pick(displayUnit, _keys);
  return create(result);
}

/**
 * Transform a DisplayUnitModel into a backend displayUnit object
 * @returns {Object}
 */
function serialize(displayUnit) {
  const result = displayUnit.asMutable({ deep: true });
  result.growable = displayUnit.isResponsive;
  delete result.isResponsive;

  result.typeRef = displayUnit.typeRef.id;
  result.format = displayUnit.channelTypeRef.id;

  return _.pick(result, _keys);
}

/** The DisplayUnitModel prototype */
const model = {
  get asset() {
    return this.isResponsive
      ? assetRefService.buildAssetRef(
        'static',
        'image',
        'auto',
        'c/img/experience-workspace/display-unit-responsive-custom.svg'
      )
      : assetRefService.buildAssetRef(
        'static',
        'image',
        'auto',
        'c/img/experience-workspace/display-unit-microsite.svg'
      );
  },
  get dimensions() {
    if (this.hasDimensions && !this.isWebUnit) {
      return this.isResponsive ? this.ratio : `${this.width}x${this.height}`;
    } else {
      return '';
    }
  },
  get embedType() {
    if (this.isResponsive && !this.hasDimensions) {
      return embedDestinationsModes.ADAPTIVE;
    }
    return this.isResponsive ? embedDestinationsModes.RESPONSIVE : embedDestinationsModes.FIXED;
  },
  get hasDimensions() {
    return !!(this.ratio || (this.width && this.height));
  },
  get isStoryUnit() {
    return this.typeRef.code === 'story';
  },
  get isWebUnit() {
    return this.typeRef.id === RefData.Enum.TYPE.DISPLAYUNIT_WEB.EMBED;
  },
  get isMicrosite() {
    return this.typeRef.id === RefData.Enum.TYPE.DISPLAYUNIT_WEB.MICROSITE;
  },
  get isOverlay() {
    return this.typeRef.id === RefData.Enum.TYPE.DISPLAYUNIT_WEB.OVERLAY;
  },
  get xDimension() {
    if (this.hasDimensions) {
      return this.isResponsive ? this.ratio.split(':')[0] : this.width;
    } else {
      return '';
    }
  },
  get yDimension() {
    if (this.hasDimensions) {
      return this.isResponsive ? this.ratio.split(':')[1] : this.height;
    } else {
      return '';
    }
  },
  get isActive() {
    return this.status.id === RefData.Enum.STATUS.DISPLAYUNIT.ACTIVE;
  },
};

/**
 * An array of keys on the API Theme object that should be shared between the API requests and responses
 */
const _keys = [
  'assetRef',
  'format',
  'growable',
  'height',
  'id',
  'name',
  'status',
  'summary',
  'typeRef',
  'width',
  'ratio',
  'byline',
  'validFor',
];

export default {
  create,
  deserialize,
  serialize,
};
